<template>
  <validation-observer ref="observer">
    <div class="modal-card" ref="modalCard">
      <b-loading :is-full-page="false" :active="isPreloading" :can-cancel="false"></b-loading>
      <header class="modal-card-head">
        <h2 class="modal-card-title">New lead</h2>
        <button class="modal-close is-large w9-annotation-layer-element"
                aria-label="close" @click="cancel"></button>
      </header>
      <section class="modal-card-body">
        <form @submit.prevent="validateForm">
          <b-field grouped class="grouped-fields-name">
            <b-field expanded class="field-first-name-wrapper">
              <validation-provider v-slot="{ errors }"
                                   name="firstName"
                                   rules="name|max:250">
                <b-field v-model="fields.firstName"
                         class="field-first-name"
                         label="First name"
                         :type="{ 'is-danger': errors.length }"
                         :message="errors + [ '&nbsp;' ]">
                  <b-input v-model="fields.firstName" maxlength="250"
                           ref="firstName"
                           @blur="()=>{ trimSpaces('firstName') }"
                           ></b-input>
                </b-field>
              </validation-provider>
            </b-field>
            <b-field expanded class="field-last-name-wrapper">
              <validation-provider v-slot="{ errors }"
                                   name="lastName"
                                   rules="name|max:250">
                <b-field v-model="fields.lastName"
                         class="field-last-name"
                         label="Last name"
                         :type="{ 'is-danger': errors.length }"
                         :message="errors + [ '&nbsp;' ]">
                  <b-input v-model="fields.lastName"
                           maxlength="250"
                           ref="lastName"
                           @blur="()=>{ trimSpaces('lastName') }"
                           ></b-input>
                </b-field>
              </validation-provider>
            </b-field>
          </b-field>
          <b-field grouped class="grouped-fields-contacts">
            <b-field expanded class="field-email-wrapper">
              <validation-provider v-slot="{ errors }"
                                   ref="emailProvider"
                                   name="email"
                                   :rules="{
                                    requiredPhone: fields.phone.length === 0,
                                    email: true,
                                    max:250,
                                    }">
                <b-field v-model="fields.email"
                         class="field-email"
                         label="E-mail"
                         :type="{ 'is-danger': errors.length }"
                         :message="errors + [ '&nbsp;' ]"
                         maxlength="250">
                  <b-input v-model="fields.email"
                           ref="email"
                           maxlength="250"
                           @blur="()=>{ trimSpaces('email') }"
                  ></b-input>
                </b-field>
              </validation-provider>
            </b-field>
            <b-field expanded class="field-phone-wrapper">
              <validation-provider
                                  v-slot="{ errors }"
                                  ref="phoneProvider"
                                  name="phone"
                                 :rules="{
                                    requiredEmail: (fields.email.length === 0),
                                    phone_with_mask: true,
                                    max: 250
                                  }">
                <b-field v-model="fields.phone"
                         class="field-phone"
                         label="Phone"
                         :type="{ 'is-danger': errors.length }"
                         :message="errors + [ '&nbsp;' ]"
                >
                  <div class="control" :class="{'has-icons-right': errors.length}">
                    <!-- todo: use ModernPhoneInput component -->
                    <CustomInput
                        ref="phone"
                        :class="{'is-danger': errors.length > 0}"
                        v-model="fields.phone"
                        :mask="masks.phone"
                        placeholder="234-567-8910"
                        @paste="phonePasteHandler"
                        :maxlength="250"
                    />
                    <label class="input-phone__prefix">+1</label>
                    <span v-if="errors.length" class="icon is-right has-text-danger">
                      <i class="mdi mdi-alert-circle mdi-24px"></i>
                    </span>
                  </div>
                </b-field>
              </validation-provider>
            </b-field>
          </b-field>

          <validation-provider
              v-slot="{ errors }"
              name="customerType"
              rules=""
          >
            <b-field
                class="field-business-type-wrapper"
                label="Customer Type"
                :type="{ 'is-danger': errors.length }"
                :message="errors + [ '&nbsp;' ]"
            >
              <b-select-ext
                  ref="customerType"
                  :selectedItem="customerType"
                  placeholder="Select..."
                  :options="customerTypeOptions"
                  :options-item-name="'value'"
                  :autocomplete="true"
                  :is-danger="!!errors.length"
                  v-bind:errors="errors"
                  @input="customerTypeInput"
                  @hideListItems="hideListItems"
              />
            </b-field>
          </validation-provider>

          <b-field>
            <validation-provider ref="companyProvider"
                                 v-slot="{ errors }"
                                 name="companyName"
                                 rules="max:240">
              <b-field v-model="fields.companyName"
                       class="field-company-name-wrapper"
                       label="Company name"
                       :type="{ 'is-danger': errors.length }"
                       :message="errors + [ '&nbsp;' ]">
                <b-input v-model.trim="fields.companyName"
                         id="input-company-name"
                         @blur="()=>{ trimSpaces('companyName') }"
                         maxlength="240"></b-input>
              </b-field>
            </validation-provider>
          </b-field>
          <b-field>
            <validation-provider v-slot="{ errors }"
                                 name="businessType"
                                 rules="max:240">
              <b-field class="field-business-type-wrapper"
                       label="Business Type"
                       :type="{ 'is-danger': errors.length }"
                       :message="errors + [ '&nbsp;' ]">
                <b-select-ext
                    ref="businessTypes"
                    :selectedItem="businessType"
                    placeholder="Select..."
                    :options="businessTypes"
                    :options-item-name="'type'"
                    :autocomplete="true"
                    :is-danger="!!errors.length"
                    v-bind:errors="errors"
                    @input="businessTypeInput"
                    @hideListItems="hideListItems"
                    @keydown.native.tab.prevent.stop="leadSourceTabKeydownNative"
                    @keydown.tab = "tabKeydown"
                    @escape = "escKeydownLeadSources"
                >
                </b-select-ext>
              </b-field>
            </validation-provider>
          </b-field>
          <b-field v-if="businessType &&
          (businessType.type === contractorBusinessType ||
          businessType.type === medicalType)">
            <validation-provider v-slot="{ errors }"
                                 name="Specialization"
                                 rules="max:240">

              <b-field class="field-business-type-wrapper"
                       label="Specialization"
                       :type="{ 'is-danger': errors.length }"
                       :message="errors + [ '&nbsp;' ]">
                <b-select-ext
                    ref="businessSpecializations"
                    :selectedItem="businessSpecialization"
                    placeholder="Select..."
                    :options="getBusinessSpecializations"
                    :autocomplete="true"
                    :is-danger="!!errors.length"
                    v-bind:errors="errors"
                    @input="businessSpecializationInput"
                    @hideListItems="hideListItems"
                    @keydown.native.tab.prevent.stop="leadSourceTabKeydownNative"
                    @keydown.tab = "tabKeydown"
                    @escape = "escKeydownLeadSources"
                >
                </b-select-ext>
              </b-field>
            </validation-provider>
          </b-field>

          <b-field class="field-lead-source-wrapper">
            <validation-provider v-slot="{ errors }"
                                 name="leadft"
                                 rules="max:240">
              <b-field label="Lead first touch"
                       class="lead-source-class"
                       v-model="leadFirstTouch"
                       :type="{ 'is-danger': errors.length }"
                       :message="errors + [ '&nbsp;' ]">
                <b-select-ext
                  ref="leadFT"
                  :selectedItem="leadFirstTouch"
                  placeholder="Select..."
                  :options="leadFirstTouches"
                  :autocomplete="true"
                  :is-danger="!!errors.length"
                  v-bind:errors="errors"
                  @input="leadFTInput"
                  @hideListItems="hideListItems"
                  @keydown.native.tab.prevent.stop="leadSourceTabKeydownNative"
                  @keydown.tab = "tabKeydown"
                  @escape = "escKeydownLeadSources"
                  >
                </b-select-ext>
              </b-field>
            </validation-provider>
          </b-field>
          <b-field class="field-lead-source-wrapper">
            <validation-provider v-slot="{ errors }"
                                 name="lead"
                                 rules="required|max:240">
              <b-field label="Lead source*"
                       class="lead-source-class"
                       v-model="leadSource"
                       :type="{ 'is-danger': errors.length }"
                       :message="errors + [ '&nbsp;' ]">
                <b-select-ext
                  ref="leadSources"
                  :selectedItem="leadSource"
                  placeholder="Select..."
                  :options="leadSources"
                  :autocomplete="true"
                  :is-danger="!!errors.length"
                  v-bind:errors="errors"
                  @input="leadInput"
                  @hideListItems="hideListItems"
                  @keydown.native.tab.prevent.stop="leadSourceTabKeydownNative"
                  @keydown.tab = "tabKeydown"
                  @escape = "escKeydownLeadSources"
                >
                </b-select-ext>
              </b-field>
            </validation-provider>
          </b-field>
          <p class="label">Partnership</p>
          <b-field class="field-partnership-wrapper" grouped>
            <b-field class="field-current-partner-program-wrapper">
              <b-select v-model="currPartnerProgramId" @input="partnerProgramChanged"
                        @keydown.native.tab.prevent.stop="partnerProgramTabKeydownNative"
                        ref="partnerProgram"
              class="current-partner-program-selector">
                <option v-for="partnerProgram in partnerPrograms"
                        :value="partnerProgram.id" :key="partnerProgram.id">
                  {{ partnerProgram.name }}
                </option>
              </b-select>
            </b-field>
            <b-field expanded class="field-partner-contractor-wrapper">
              <validation-provider v-slot="{ errors }"
                                   name="partner"
                                   :rules="{ required:currPartnerProgramId>0 }">
                <b-field expanded v-model="partnershipContractor"
                         class="field-partner-contractor"
                         :type="{ 'is-danger': errors.length }"
                         :message="errors + [ '&nbsp;' ]"
                         >
                  <b-select-ext
                    ref="partners"
                    :selectedItem="partnershipContractor"
                    :placeholder="currPartnerProgramId > 0 ? 'Select...':''"
                    :is-danger="!!errors.length"
                    :disabled="currPartnerProgramId === 0"
                    :options="partners"
                    :autocomplete="true"
                    :has-search-button="true"
                    :search-hint="partnerSearchHint"
                    @input="partnerInput"
                    @hideListItems="hideListItems"
                    @keydown.native.tab.prevent.stop="tabKeydownNativeDoSearch"
                    @keydown.tab="tabKeydownDoSearch"
                    v-on:search="doSearch"
                    @escape="escKeydownPartners"
                  >
                    <template #optionContent="{ option }">
                      <template v-if="option.id === -1">{{ option.name }}</template>
                      <span v-else class="partner-option">
                        {{ option.name }}
                        <span v-if="option.companyName"
                              class="partner-option__company">{{ option.companyName }}</span>
                      </span>
                    </template>
                  </b-select-ext>
                </b-field>
              </validation-provider>
            </b-field>
          </b-field>
          <b-field>
            <b-checkbox
              v-model="fields.isAssignOwner"
              style="font-size: 1rem; font-weight: 400;"
              class="field-partnership-wrapper is-grouped"
              @input="changeAssignOwner"
            >
              Assign an owner using lead distribution
            </b-checkbox>
          </b-field>
          <b-field disabled v-model="leadOwner"
                   class="field-lead-owner"
                   label="Lead owner">
            <b-input disabled v-model="leadOwner"></b-input>
          </b-field>
        </form>
      </section>
      <footer class="modal-card-foot">
        <button class="button button-cancel" type="button" @click="cancel">Cancel</button>
        <button id="btn-is-success" class="button is-success"
                @keydown.tab.prevent.stop="tabKeydownNativeSuccess"
                @click="submitForm()">Create</button>
      </footer>
    </div>
  </validation-observer>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex';
import { createLead, getContractors } from '@/api/contractor';
import { searchPartners } from '@/api/partner';
import * as MUTATIONS from '../../store/mutations';
import BSelectExt from '../BSelectExt.vue';
import CustomInput from '../CustomInput.vue';

const emptyOptionId = 'empty-option';
export default {
  components: {
    CustomInput,
    BSelectExt,
  },
  name: 'CreateNewLead',
  props: ['active', 'leadSources', 'leadOwner', 'leadFirstTouches', 'businessTypes', 'businessSpecializations', 'customerTypes'],
  data() {
    return {
      contractorBusinessType: 'Contractor Business',
      medicalType: 'Medical',
      customSelectors: ['partners', 'leadSources', 'businessTypes', 'businessSpecializations', 'leadFT'],
      leadSource: null,
      businessType: null,
      businessSpecialization: null,
      leadFirstTouch: null,
      partnershipContractor: null,
      customerType: null,
      customerTypeOptions: [],
      fields: {
        id: '',
        companyName: '',
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        isAssignOwner: false,
      },
      masks: {
        phone: {
          prefix: '',
          delimiters: ['-', '-', ''],
          blocks: [3, 3, 4],
          numericOnly: true,
        },
      },
      leadOwnerOld: '',
      isPreloading: false,
      partners: [],
      searchText: '',
      page: 1,
      perPage: 20,
      currPartnerProgramId: 0,
      partnerPrograms: [
        { id: 0, name: 'None' },
        { id: 1, name: 'Partner' },
        { id: 2, name: 'Referral' },
      ],
    };
  },
  methods: {
    ...mapMutations({ preloader: MUTATIONS.SET_PRELOADER }),
    async validateForm() {
      const valid = await this.$refs.observer.validate();
      if (valid) {
        this.submit();
      } else {
        if (!this.$refs.observer.fields.phone.valid && this.fields.phone.length > 0) {
          this.$refs.phone.focus();
        }
        if (!this.$refs.observer.fields.email.valid) {
          this.$refs.email.focus();
        }
        if (!this.$refs.observer.fields.lastName.valid) {
          this.$refs.lastName.focus();
        }
        if (!this.$refs.observer.fields.firstName.valid) {
          this.$refs.firstName.focus();
        }
      }
    },
    changeAssignOwner() {
      if (this.fields.isAssignOwner === true) {
        this.leadOwner = '';
      } else {
        this.leadOwner = this.leadOwnerOld;
      }
    },
    submitForm() {
      this.validateForm();
    },
    escKeydownLeadSources() {
      this.$refs.leadSources.$refs.mainSpanSelector.focus();
    },
    escKeydownPartners() {
      this.$refs.partners.$refs.mainSpanSelector.focus();
    },
    tabKeydown() {
      this.hideListItems();
      this.$refs.partnerProgram.focus();
    },
    leadSourceTabKeydownNative(event) {
      if (event.shiftKey) {
        const elem = document.getElementById('input-company-name');
        elem.focus();
      } else {
        this.hideListItems();
        this.$refs.partnerProgram.focus();
      }
    },
    tabKeydownDoSearch() {
      this.hideListItems();
      const btnSuccess = document.getElementById('btn-is-success');
      btnSuccess.focus();
    },
    tabKeydownNativeDoSearch(event) {
      if (event.shiftKey) {
        this.$refs.partnerProgram.focus();
      } else {
        const btnSuccess = document.getElementById('btn-is-success');
        btnSuccess.focus();
      }
    },
    leadInput(val) {
      this.leadSource = val;
      this.$refs.leadSources.$refs.mainSpanSelector.focus();
    },
    businessTypeInput(val) {
      this.businessType = val;
      this.businessSpecialization = null;
    },
    customerTypeInput(val) {
      this.customerType = val;
    },
    businessSpecializationInput(val) {
      this.businessSpecialization = val;
    },
    leadFTInput(val) {
      this.leadFirstTouch = val;
      this.$refs.leadFT.$refs.mainSpanSelector.focus();
    },
    partnerProgramChanged() {
      this.partnershipContractor = null;
      this.partners = [];
      this.searchText = '';
      if (this.$refs.partners) {
        this.$refs.partners.$data.searchText = '';
      }
    },
    partnerProgramTabKeydownNative(event) {
      if (event.shiftKey) {
        this.$refs.leadSources.$refs.mainSpanSelector.focus();
      } else if (this.currPartnerProgramId === 0) {
        const btnSuccess = document.getElementById('btn-is-success');
        btnSuccess.focus();
      } else {
        this.$refs.partners.$refs.mainSpanSelector.focus();
      }
    },
    tabKeydownNativeSuccess(event) {
      if (event.shiftKey) {
        if (this.currPartnerProgramId === 0) {
          this.$refs.partnerProgram.focus();
        } else {
          this.$refs.partners.$refs.mainSpanSelector.focus();
        }
      } else {
        const closeBtn = document.querySelector('button.modal-close');
        closeBtn.focus();
      }
    },
    trimSpaces(field) {
      this.fields[field] = this.fields[field].replace(/([\s]+)/gu, ' ').replace(/^[\s]|[\s]$/gu, '');
    },
    partnerInput(val) {
      this.partnershipContractor = val;
      this.partners = [];
      this.$refs.partners.$refs.mainSpanSelector.focus();
    },
    doSearch(searchText) {
      if (searchText.length >= 3) {
        this.searchText = searchText;
        this.loadAsyncData();
      }
    },
    async submit() {
      this.isPreloading = true;
      const obj = { ...this.$data.fields };
      const phoneDigits = obj.phone.replaceAll('-', '');
      obj.phone = phoneDigits ? `1${phoneDigits}` : '';
      if (this.currPartnerProgramId === 1) {
        obj.partnerId = this.partnershipContractor.id;
      } else if (this.currPartnerProgramId === 2) {
        obj.referralId = this.partnershipContractor.id;
      } else {
        delete obj.partnerProgram;
      }
      if (this.leadSource) {
        obj.leadSourceId = this.leadSource.id;
      }
      if (this.leadFirstTouch) {
        obj.leadFirstTouchId = this.leadFirstTouch.id;
      }
      if (this.businessType) {
        obj.businessType = this.businessType.id;
      }
      if (this.businessSpecialization) {
        obj.businessSpecialization = this.businessSpecialization.id;
      }
      if (this.customerType && this.customerType.id !== emptyOptionId) {
        obj.customerTypeId = this.customerType.id;
      }

      try {
        const { data } = await createLead(obj);
        this.$emit('submit', data);
      } catch (exception) {
        if (exception.response && exception.response.status === 422) {
          this.$emit('error', exception.response);
        } else {
          throw exception;
        }
      } finally {
        this.isPreloading = false;
      }
    },
    /**
     * Fetch partners or contractors data depending on the selected partner program
     */
    loadAsyncData() {
      const isPartnerProgram = this.currPartnerProgramId === 1;
      const queryParams = {
        page: this.page,
        limit: this.perPage,
        searchText: this.searchText ? this.searchText.trim() : this.searchText,
      };
      if (!isPartnerProgram) {
        queryParams.order = '+lastName,+firstName';
      }
      const request = isPartnerProgram ? searchPartners(queryParams) : getContractors(queryParams);
      request.then((response) => {
        this.partners = [];
        response.data.data.forEach((elem, index) => {
          this.partners[index] = elem;
          this.partners[index].name = `${elem.firstName} ${elem.lastName} [${elem.id}]`;
        });
        if (response.data.total === 0) {
          this.partners.push({ id: -1, name: 'Nothing found...' });
        }
        if (response.data.total > this.perPage) {
          this.partners.push({ id: -1, name: 'Too many results. Refine your search criteria.' });
        }
      }).catch(() => {
        this.partners = [];
      });
    },
    cancel() {
      this.$emit('close');
    },
    hideListItems(vueComp = null) {
      if (!vueComp) {
        this.customSelectors.forEach((item) => {
          if (this.$refs[item]) {
            this.$refs[item].$data.showListItems = false;
          }
        });
        return;
      }
      Object.keys(this.$refs).forEach((item) => {
        if (this.customSelectors.includes(item)
            && this.$refs[item]
            && this.$refs[item] !== vueComp) {
          this.$refs[item].$data.showListItems = false;
        }
      });
    },
    keydownHandler(event) {
      if (event.code === 'Escape') {
        this.$emit('close');
      }
    },
    partnerProgramKeydownHandler(event) {
      if (event.code === 'ArrowLeft' || event.code === 'ArrowRight') event.preventDefault();
    },
    phonePasteHandler(event) {
      const regEx = /[^\d]+/g;
      const replacedValue = event.clipboardData.getData('text').replace(regEx, '');
      if (replacedValue.length > 9) {
        this.$refs.phone.cleave.setRawValue(replacedValue.slice(-10));
      }
    },
  },
  mounted() {
    this.$refs.firstName.focus();
    const animationCont = document.querySelector('.animation-content');
    animationCont.style['max-width'] = '665px';
    animationCont.style.width = '100%';
    this.$refs.modalCard.addEventListener('keydown', this.keydownHandler);
    this.$refs.partnerProgram.$el.addEventListener('keydown', this.partnerProgramKeydownHandler);
    this.leadOwnerOld = this.leadOwner;

    this.customerTypeOptions = [{ id: emptyOptionId, value: '\u00A0' }, ...this.customerTypes];
  },
  beforeDestroy() {
    this.$refs.modalCard.removeEventListener('keydown', this.keydownHandler);
    this.$refs.partnerProgram.$el.removeEventListener('keydown', this.partnerProgramKeydownHandler);
  },
  computed: {
    ...mapGetters(['authAuthenticator']),
    ...mapState('user', {
      userId: (state) => state.id,
    }),
    partnerSearchHint() {
      if (this.currPartnerProgramId === 1) {
        return 'Search by first or last name, company, or partner ID';
      }
      if (this.currPartnerProgramId === 2) {
        return 'Search by first or last name, company, or customer ID';
      }
      return '';
    },
    getBusinessSpecializations() {
      const array = this.businessSpecializations.filter(
        (item) => item.businessType.id === this.businessType.id,
      ).sort((a, b) => a.name.localeCompare(b.name));
      const index = array.findIndex((item) => item.name === 'Other');
      if (index !== -1) {
        const [item] = array.splice(index, 1);
        array.push(item);
      }
      return array;
    },
  },
};
</script>

<style scoped lang="stylus">
@require "../../assets/style/modern/variables.styl";
.field >>> input.input
  height 48px
.field >>> .select select
  height 48px
.field >>> input.input
  &[disabled='disabled']
    border-color #dfdfdf!important
    height 48px
.field >>> input.input::placeholder
  color #8a9499
.field-phone >>> input.input
  padding-left 33px
  font-size 19px
.input-phone__prefix
  display block
  position absolute
  font-size 19px
  font-weight 400
  left 12px
  top 10px
  pointer-events none
.control >>> .select.input
  &:not(.is-danger)
    border-color #8FA7B3!important
  &.disabled
    border-color #dfdfdf!important

.field >>> .label,.label
  font-weight 400

.control >>> span.select.select
  font-size 19px
  paddind-top 16px
.control >>> span.select.select
  &:after
    border-color #525f66
    top 24px
.modal-card
  width 100%
  @media screen and (max-width 390px)
    max-height 650px
  @media screen and (max-height 450px)
    max-height 300px

.modal-card-title
  margin-bottom 0
  font-size 30px

.modal-card-body
  max-height 610px;

.field.grouped-fields-name,
.field.grouped-fields-contacts,
.field-company-name-wrapper,
.field-lead-source-wrapper,
.field-lead-owner
  max-width 625px

.field-partner-contractor-wrapper
  width 30%
.grouped-fields-name,.grouped-fields-contacts
  column-gap 13px

.field-email-wrapper,.field-phone-wrapper
  flex-basis 50%

.field-first-name-wrapper,.field-last-name-wrapper
  width 100%

.control.current-partner-program-selector >>> span.select
  margin-right 3px
  select
    width 150px
    border-color #8FA7B3

.field-partner-contractor >>> .partner-option
  display block
  padding 5px 0
  font-size 16px
  color #293033
.field-partner-contractor >>> .partner-option__company
  display block
  font-size 14px
  line-height 1.2
  color #525F66
.field-partner-contractor >>> .list-box-item:hover .partner-option,
.field-partner-contractor >>> .list-box-item.is-focused .partner-option
  color inherit
  .partner-option__company
    color inherit

.field-partner-contractor >>> span.select.select-out,
.field-lead-source-wrapper >>> span.select.select-out
  height 48px
  font-size 19px
  font-weight 400

.field.field-partnership-wrapper.is-grouped
  max-width: 625px
  display flex
  flex-wrap wrap

footer.modal-card-foot
  padding 12px
  justify-content end
button.button
  width 135px
  height 46px
  border 1px solid #23D160
  font-size 19px
  text-transform uppercase
.button.button-cancel
  color #23D160
.button.button-cancel:hover
  background #17b548
  color white
.button.is-success
  border none
</style>
